import type { EngagementManager } from './EngagementManager.model'

/**
 * Workflow status of one timesheet entry. `DRAFT` is displayed as `-`.
 *
 * There is deliberately no rejected status: a manager who disagrees asks the member to edit the entry,
 * which returns it to draft, or escalates to an administrator.
 */
export enum TimesheetEntryStatus {
    APPROVED = 'APPROVED',
    DRAFT = 'DRAFT',
    SUBMITTED = 'SUBMITTED',
}

/** The caller's relationship to a timesheet, resolved server-side and returned on every response. */
export enum TimesheetViewerRole {
    ADMINISTRATOR = 'ADMINISTRATOR',
    MANAGER = 'MANAGER',
    MEMBER = 'MEMBER',
    TM = 'TM',
}

export interface TimesheetEntry {
    approvalComment: string | null
    approvedAt: string | null
    approvedByHandle: string | null
    /** Exact decimal string, e.g. `8.50`. Hours become a payment amount, so they never travel as floats. */
    hoursWorked: string
    id: string
    isPaid: boolean
    /** Payment that consumed this entry; null while still unpaid. */
    paymentReference: string | null
    /** When the entry was linked to the payment that consumed it. */
    paidAt: string | null
    /** True when the work date falls outside the assignment's start/end dates. Allowed, but flagged. */
    outsideAssignmentWindow: boolean
    remarks: string | null
    /** Set when an administrator reopened a previously approved entry; the entry is back in draft. */
    reopenedAt: string | null
    status: TimesheetEntryStatus
    submittedAt: string | null
    /** `YYYY-MM-DD`. `DD-MM-YYYY` is display only. */
    workDate: string
}

export interface TimesheetAssignment {
    endDate: string | null
    /**
     * Total hours minus hours on processed payments. Null when there is no total, on the member's own
     * view, or when payments could not be read.
     */
    hoursLeft: number | null
    id: string
    memberHandle: string
    memberId: string
    memberName: string | null
    standardHoursPerDay: number | null
    startDate: string | null
    status: string
    /** Hours allocated to the assignment overall. */
    totalHours: number | null
}

export interface TimesheetView {
    assignment: TimesheetAssignment
    engagementId: string
    engagementTitle: string
    entries: TimesheetEntry[]
    managers: EngagementManager[]
    viewerRole: TimesheetViewerRole
}

export interface UpsertTimesheetEntryRequest {
    hoursWorked: string
    remarks?: string | null
    workDate: string
}

export interface UpsertTimesheetEntriesRequest {
    entries: UpsertTimesheetEntryRequest[]
    overrideReason?: string
}

export interface SubmitTimesheetEntriesRequest {
    entryIds: string[]
    overrideReason?: string
}

export interface ApproveTimesheetEntriesRequest {
    approvalComment: string
    entryIds: string[]
    overrideReason?: string
}

export interface ReopenTimesheetEntriesRequest {
    entryIds: string[]
    overrideReason: string
}

export interface SkippedTimesheetEntry {
    approvedByHandle: string | null
    currentStatus: TimesheetEntryStatus
    id: string
}

/**
 * Approval is partial by design: a second manager approving the same selection gets the entries the
 * first one already took back in `skipped`, rather than an error for the whole batch.
 */
export interface ApproveTimesheetEntriesResult {
    approved: string[]
    skipped: SkippedTimesheetEntry[]
}

export interface TimesheetQuery {
    fromDate?: string
    status?: TimesheetEntryStatus
    toDate?: string
}

/**
 * Rolled-up per-assignee status on the manager and administrator landing lists. `Not Submitted` means
 * nothing is awaiting approval and nothing has been approved yet; it is a display value only, not a
 * filter option.
 */
export type TimesheetRollupStatus = 'Approved' | 'Not Submitted' | 'Pending Approval'

export interface TimesheetEngagementRow {
    assigneeHandle: string
    assigneeId: string
    assigneeName: string | null
    assignmentId: string
    /** The assignment's own status. A manager's list only carries `ASSIGNED` and `COMPLETED`. */
    assignmentStatus: string
    engagementId: string
    engagementTitle: string
    /** True when at least one entry is submitted and awaiting approval. */
    hasPendingApproval: boolean
    timesheetStatus: TimesheetRollupStatus
    viewerRole: TimesheetViewerRole
}

export interface TimesheetEngagementQuery {
    assignee?: string
    fromDate?: string
    manager?: string
    page?: number
    perPage?: number
    status?: TimesheetRollupStatus
    title?: string
    toDate?: string
}

export interface TimesheetAuditRecord {
    action: string
    actorHandle: string | null
    actorRole: string
    actorUserId: string
    comment: string | null
    createdAt: string
    id: string
    previousValues: unknown
    updatedValues: unknown
}

export interface TimesheetEngagementListResponse {
    data: TimesheetEngagementRow[]
    meta: {
        page: number
        perPage: number
        totalCount: number
        totalPages: number
        /** The caller's own role, so the client never infers it from JWT roles. */
        viewerRole: TimesheetViewerRole
    }
}
